import React, { useEffect, useMemo, useRef, useState } from "react";
import FieldError from "../FormFields/FieldError";
import FieldLabel from "../FormFields/FieldLabel";
import { useLanguage } from "../../context/LanguageContext";
import {
  getLocalizedPhoneCountryOptions,
  getPhoneCountryOption,
  sanitizeNationalPhone,
} from "../../utils/phoneCountryOptions";
import chevronDark from "../../assets/images/chevron-down-dark.svg";

const PhoneField = ({
  id,
  label,
  required = true,
  placeholder = "50 123 4567",
  countryCode,
  phone,
  onCountryChange,
  onPhoneChange,
  onBlur,
  error = "",
  className = "",
}) => {
  const { t, locale } = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const phoneOptions = useMemo(() => getLocalizedPhoneCountryOptions(locale), [locale]);
  const selected = getPhoneCountryOption(countryCode, phoneOptions);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return phoneOptions;
    return phoneOptions.filter((option) =>
      String(option.searchLabel || "").includes(term)
    );
  }, [phoneOptions, search]);

  const emitPhone = (value) => {
    onPhoneChange({ target: { value } });
  };

  const applyNationalPhone = (raw, dialCode = selected.dialCode) => {
    emitPhone(sanitizeNationalPhone(raw, dialCode, phoneOptions));
  };

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    searchRef.current?.focus();

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleSelect = (code) => {
    const next = getPhoneCountryOption(code, phoneOptions);
    onCountryChange(code);
    applyNationalPhone(phone, next.dialCode);
    setOpen(false);
    setSearch("");
  };

  const toggleOpen = () => {
    setOpen((prev) => {
      if (prev) setSearch("");
      return !prev;
    });
  };

  return (
    <div className={`signup-form__field signup-form__field--phone${className ? ` ${className}` : ""}`}>
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>

      <div className="signup-form__phone" ref={wrapRef}>
        <div className={`signup-form__phone-input${error ? " is-invalid" : ""}`}>
          <button
            type="button"
            className="signup-form__phone-country"
            aria-label={`${selected.label} +${selected.dialCode}`}
            aria-expanded={open}
            aria-haspopup="listbox"
            onClick={toggleOpen}
          >
            <img
              className="signup-form__phone-flag"
              src={selected.flagUrl}
              alt=""
              width={24}
              height={16}
            />
            <span className="signup-form__phone-dial">+{selected.dialCode}</span>
            <img
              className="signup-form__phone-chevron"
              src={chevronDark}
              alt=""
              width={12}
              height={12}
              aria-hidden="true"
            />
          </button>

          <input
            id={id}
            className="signup-form__input signup-form__phone-number"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder={placeholder}
            value={phone}
            onChange={(event) => applyNationalPhone(event.target.value)}
            onBlur={onBlur}
            onPaste={(event) => {
              event.preventDefault();
              applyNationalPhone(event.clipboardData.getData("text"));
            }}
            required={required}
            maxLength={15}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
          />
        </div>

        {open ? (
          <div className="signup-form__phone-dropdown">
            <input
              ref={searchRef}
              type="text"
              className="signup-form__select-search"
              placeholder={t("signup.search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t("signup.searchLabel", { label: t("signup.countryCodes") })}
            />
            <ul className="signup-form__phone-list" role="listbox" aria-label={t("signup.countryCodes")}>
              {filteredOptions.length ? (
                filteredOptions.map((option) => (
                  <li key={option.code} role="presentation">
                    <button
                      type="button"
                      className={`signup-form__phone-option${
                        option.code === selected.code ? " is-selected" : ""
                      }`}
                      role="option"
                      aria-selected={option.code === selected.code}
                      onClick={() => handleSelect(option.code)}
                    >
                      <img
                        className="signup-form__phone-option-flag"
                        src={option.flagUrl}
                        alt=""
                        width={24}
                        height={16}
                      />
                      <span className="signup-form__phone-option-label">
                        {option.label}
                      </span>
                      <span className="signup-form__phone-option-code">+ {option.dialCode}</span>
                    </button>
                  </li>
                ))
              ) : (
                <li className="signup-form__select-empty" role="presentation">
                  {t("signup.noResults")}
                </li>
              )}
            </ul>
          </div>
        ) : null}
      </div>
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
};

export default PhoneField;
